// A14 — Feature flag va remote config. Module-local data layer for the two
// missing screens A14.11 (A/B test natijalari) + A14.15 (klientga tarqatish
// holati). Does NOT touch `lib/adapters.ts` / `i18n/*` (module-local `FF_STR`
// strings — REGISTER pattern, developers/devApi.ts bilan bir xil). Fail-soft.
//   • A14.11 — natija-metrikasi (konversiya/p-value) telemetriya kutadi
//     (Experiments.tsx izohi) → endpoint greenfield ⇒ fail-soft bo'sh.
//   • A14.15 — REAL manba `GET /admin/settings/flags` (rollout% qiymatdan hosil).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** A14.11 — bitta A/B eksperiment-variant natijasi (konversiya + ahamiyat). */
export interface AbResultRow {
  experiment: string
  variant: string
  users: number
  conversions: number
  /** Konversiya ulushi (0..1). */
  conversion_rate: number
  p_value: number
  significant: boolean
}

/**
 * A14.11 — Test natijalari (statistik ahamiyat). Konversiya + p-value + ahamiyat.
 * Backend natija-metrikasi telemetriya kutadi (`Experiments.tsx` izohи: «natija-
 * metrika EMAS — u telemetriya kutadi») → endpoint greenfield, fail-soft bo'sh.
 * Wired: `GET /admin/settings/experiments/results` → `{ results: AbResultRow[] }`.
 */
export function fetchAbResults(params: FetchParams): Promise<FetchResult<AbResultRow>> {
  return safeList<AbResultRow>(
    '/admin/settings/experiments/results',
    'results',
    params.signal,
  ).then((rows) => toResult(rows, params, (r) => `${r.experiment} ${r.variant}`))
}

/** A14.15 — flag/config uchun klientga tarqatish holati (flagdan hosil qilinadi). */
export interface RolloutStatusRow {
  key: string
  /** Rollout foizi 0..100. */
  rollout_pct: number
  platforms: string
  status: 'off' | 'rolling' | 'full'
  enabled: boolean
  updated_at: number
}

interface RawFlag {
  key: string
  value: string
  enabled: boolean
  updated_at: number
}

/** Flag qiymatidan rollout foizini chiqaradi ("50%", "50", "true"/"false"). */
function rolloutPct(value: string, enabled: boolean): number {
  if (!enabled) return 0
  const s = (value ?? '').trim()
  const m = s.match(/-?\d+(?:\.\d+)?/)
  if (m) {
    const n = Number(m[0])
    if (!Number.isNaN(n)) return Math.max(0, Math.min(100, n))
  }
  if (s === 'false') return 0
  return 100 // yoqilgan bool / boshqa qiymat → to'liq tarqatilgan
}

/**
 * A14.15 — Klientga tarqatish holati. REAL manba: `GET /admin/settings/flags`
 * (`{ flags: RawFlag[] }`). Har flag → rollout% (qiymatdan) + holat
 * (off/rolling/full). Platforma-segment alohida endpoint (`/flags/segment`) →
 * bu ro'yxatда "all" (segment yo'q = barcha platforma). Fail-soft bo'sh.
 */
export function fetchRolloutStatus(
  params: FetchParams,
): Promise<FetchResult<RolloutStatusRow>> {
  return safeList<RawFlag>('/admin/settings/flags', 'flags', params.signal).then((flags) => {
    const rows: RolloutStatusRow[] = flags.map((f) => {
      const pct = rolloutPct(f.value, f.enabled)
      const status: RolloutStatusRow['status'] = !f.enabled
        ? 'off'
        : pct >= 100
          ? 'full'
          : pct > 0
            ? 'rolling'
            : 'off'
      return {
        key: f.key,
        rollout_pct: pct,
        platforms: 'all',
        status,
        enabled: f.enabled,
        updated_at: f.updated_at,
      }
    })
    return toResult(rows, params, (r) => r.key)
  })
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER pattern). */
export const FF_STR = {
  // A14.11 — A/B test natijalari
  resultsTitle: 'Результаты A/B-тестов (значимость)',
  experiment: 'Эксперимент',
  variant: 'Вариант',
  users: 'Пользователей',
  conversions: 'Конверсий',
  conversionRate: 'Конверсия',
  pValue: 'p-значение',
  significance: 'Значимость',
  significant: 'Значимо',
  notSignificant: 'Не значимо',
  // A14.15 — klientга tarqatish holati
  rolloutTitle: 'Статус раскатки на клиентов',
  key: 'Ключ флага/конфига',
  rolloutPct: 'Раскатка, %',
  platforms: 'Платформы',
  status: 'Статус',
  stOff: 'Выключен',
  stRolling: 'Раскатывается',
  stFull: 'Полностью',
  updated: 'Обновлён',
} as const
